/**
 * Branch ACL panel used in the Edit Referrer dialog (solicitor-side, Phase 1).
 *
 * Lets the solicitor:
 *   - CRUD branches under the open referrer's company.
 *   - Pick the referrer's home branch + ACL (allowed_branches).
 *   - Toggle branch-admin flags per branch.
 *
 * Cases / quotes / invoices on the broker portal are filtered by the
 * referrer's home_branch ∪ allowed_branches set, so the solicitor controls
 * cross-branch visibility from one place.
 */
import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, Buildings, Check, X, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import API from "../../lib/api";

export default function ReferrerBranchesPanel({ referrer, onSaved }) {
  const company = (referrer?.company || "").trim();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  // Bulk-move state — when set, this branch row swaps to a "Move members
  // to..." picker bar so the solicitor can reorganise offices without
  // touching each referrer one by one.
  const [movingBranchId, setMovingBranchId] = useState(null);
  const [moveTargetId, setMoveTargetId] = useState("");
  const [movingNow, setMovingNow] = useState(false);
  // Delete state — when the branch has members, deletion swaps the row to a
  // confirm bar with an optional reassign dropdown so the solicitor never
  // accidentally orphans brokers.
  const [deletingBranchId, setDeletingBranchId] = useState(null);
  const [deleteReassignId, setDeleteReassignId] = useState("");
  const [deletingNow, setDeletingNow] = useState(false);
  // Per-referrer ACL state.
  const [homeBranch, setHomeBranch] = useState(referrer?.branch_id || "");
  const [allowed, setAllowed] = useState(new Set(referrer?.allowed_branches || []));
  const [adminOf, setAdminOf] = useState(new Set(referrer?.branch_admin_of || []));
  const [savingAcl, setSavingAcl] = useState(false);

  const fetchBranches = async () => {
    if (!company) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/branches?company=${encodeURIComponent(company)}`);
      setBranches(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load branches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBranches(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [company]);
  // Reset ACL state whenever the referrer prop swaps.
  useEffect(() => {
    setHomeBranch(referrer?.branch_id || "");
    setAllowed(new Set(referrer?.allowed_branches || []));
    setAdminOf(new Set(referrer?.branch_admin_of || []));
  }, [referrer?.id, referrer?.branch_id, referrer?.allowed_branches, referrer?.branch_admin_of]);

  if (!company) {
    return (
      <div className="text-xs text-slate-500 italic p-4 border border-dashed border-slate-200 rounded-md">
        Add a company on the Details tab before configuring branches.
      </div>
    );
  }

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await API.post("/branches", { company, name: newName.trim(), address: newAddress.trim() || undefined });
      toast.success(`Branch "${newName.trim()}" created`);
      setNewName(""); setNewAddress("");
      await fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create branch");
    } finally {
      setCreating(false);
    }
  };

  const beginEdit = (b) => {
    setEditingBranchId(b.id);
    setEditName(b.name);
    setEditAddress(b.address || "");
  };
  const cancelEdit = () => { setEditingBranchId(null); };
  const commitEdit = async () => {
    if (!editName.trim()) return;
    try {
      await API.put(`/branches/${editingBranchId}`, { name: editName.trim(), address: editAddress });
      toast.success("Branch saved");
      setEditingBranchId(null);
      await fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save branch");
    }
  };
  const beginMove = (b) => {
    setMovingBranchId(b.id);
    setMoveTargetId("");
  };
  const cancelMove = () => { setMovingBranchId(null); setMoveTargetId(""); };
  const commitMove = async (b) => {
    setMovingNow(true);
    try {
      const { data } = await API.post(`/branches/${b.id}/move-members`, {
        to_branch_id: moveTargetId || null,
      });
      const targetName = moveTargetId
        ? (branches.find((x) => x.id === moveTargetId)?.name || "branch")
        : "(no branch)";
      toast.success(`Moved ${data.moved} member${data.moved === 1 ? "" : "s"} from "${b.name}" to "${targetName}"`);
      cancelMove();
      // Refresh + push the panel state in sync so the home-dropdown reflects
      // any change to the open referrer (if they were in the cohort).
      await fetchBranches();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to move members");
    } finally {
      setMovingNow(false);
    }
  };
  const handleDeleteBranch = async (b) => {
    // For branches with no members, skip the reassign picker — just confirm
    // and go. (Same UX as the original "fast path".)
    if (!b.member_count) {
      if (!window.confirm(`Delete branch "${b.name}"?`)) return;
      try {
        await API.delete(`/branches/${b.id}`);
        toast.success("Branch deleted");
        if (homeBranch === b.id) setHomeBranch("");
        setAllowed((s) => { const n = new Set(s); n.delete(b.id); return n; });
        setAdminOf((s) => { const n = new Set(s); n.delete(b.id); return n; });
        await fetchBranches();
        if (onSaved) onSaved();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to delete branch");
      }
      return;
    }
    // Otherwise pop the inline reassign-confirm bar so the solicitor decides
    // where the displaced members go.
    setDeletingBranchId(b.id);
    setDeleteReassignId("");
  };
  const cancelDelete = () => { setDeletingBranchId(null); setDeleteReassignId(""); };
  const commitDelete = async (b) => {
    setDeletingNow(true);
    try {
      // 1. Move (or unassign) members first — keeps "delete with members" safe.
      await API.post(`/branches/${b.id}/move-members`, {
        to_branch_id: deleteReassignId || null,
      });
      // 2. Now the branch is empty — drop it.
      await API.delete(`/branches/${b.id}`);
      const targetName = deleteReassignId
        ? (branches.find((x) => x.id === deleteReassignId)?.name || "another branch")
        : "no branch (unassigned)";
      toast.success(`Deleted "${b.name}" — ${b.member_count} member${b.member_count === 1 ? "" : "s"} reassigned to ${targetName}`);
      cancelDelete();
      if (homeBranch === b.id) setHomeBranch(deleteReassignId || "");
      setAllowed((s) => { const n = new Set(s); n.delete(b.id); return n; });
      setAdminOf((s) => { const n = new Set(s); n.delete(b.id); return n; });
      await fetchBranches();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete branch");
    } finally {
      setDeletingNow(false);
    }
  };

  const toggleAllowed = (bid) => {
    setAllowed((s) => { const n = new Set(s); if (n.has(bid)) n.delete(bid); else n.add(bid); return n; });
  };
  const toggleAdmin = (bid) => {
    setAdminOf((s) => { const n = new Set(s); if (n.has(bid)) n.delete(bid); else n.add(bid); return n; });
  };

  const saveAcl = async () => {
    setSavingAcl(true);
    try {
      // Strip the home branch from `allowed_branches` server-side too — it's
      // always implicit. Keeping the list lean keeps the DB readable.
      const allowedList = Array.from(allowed).filter((bid) => bid !== homeBranch);
      await API.put(`/referrers/${referrer.id}/branch-acl`, {
        branch_id: homeBranch || null,
        allowed_branches: allowedList,
        branch_admin_of: Array.from(adminOf),
      });
      toast.success("Branch access updated");
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingAcl(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Branch list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold flex items-center gap-1.5">
            <Buildings weight="bold" className="w-4 h-4 text-indigo-600" />
            Branches in {company}
          </Label>
          <span className="text-[11px] text-slate-400">{branches.length} branch{branches.length === 1 ? "" : "es"}</span>
        </div>
        {loading ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : branches.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No branches yet — create one below.</p>
        ) : (
          <ul className="space-y-2" data-testid="branches-list">
            {branches.map((b) => (
              <li key={b.id} className="border border-slate-200 rounded-md p-2.5 flex items-center justify-between gap-2" data-testid={`branch-row-${b.id}`}>
                {deletingBranchId === b.id ? (
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-xs text-rose-700 shrink-0">
                      Delete <strong>{b.name}</strong> — reassign <strong>{b.member_count}</strong> member{b.member_count === 1 ? "" : "s"} to
                    </span>
                    <select
                      value={deleteReassignId}
                      onChange={(e) => setDeleteReassignId(e.target.value)}
                      className="flex-1 h-8 px-2 rounded border border-rose-200 text-xs bg-white"
                      data-testid={`delete-reassign-${b.id}`}
                    >
                      <option value="">— Leave unassigned —</option>
                      {branches.filter((x) => x.id !== b.id).map((x) => (
                        <option key={x.id} value={x.id}>{x.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => commitDelete(b)}
                      disabled={deletingNow}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-rose-100 text-rose-600 disabled:opacity-40"
                      title="Delete branch"
                      data-testid={`delete-commit-${b.id}`}
                    ><Trash weight="bold" className="w-3.5 h-3.5" /></button>
                    <button
                      onClick={cancelDelete}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500"
                      title="Cancel"
                      data-testid={`delete-cancel-${b.id}`}
                    ><X weight="bold" className="w-3.5 h-3.5" /></button>
                  </div>
                ) : movingBranchId === b.id ? (
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-xs text-slate-600 shrink-0">
                      Move <strong>{b.member_count}</strong> member{b.member_count === 1 ? "" : "s"} of <strong>{b.name}</strong> to
                    </span>
                    <select
                      value={moveTargetId}
                      onChange={(e) => setMoveTargetId(e.target.value)}
                      className="flex-1 h-8 px-2 rounded border border-slate-200 text-xs bg-white"
                      data-testid={`move-target-${b.id}`}
                    >
                      <option value="">— No branch (unassign) —</option>
                      {branches.filter((x) => x.id !== b.id).map((x) => (
                        <option key={x.id} value={x.id}>{x.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => commitMove(b)}
                      disabled={movingNow}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-emerald-100 text-emerald-600 disabled:opacity-40"
                      title="Move members"
                      data-testid={`move-commit-${b.id}`}
                    ><Check weight="bold" className="w-3.5 h-3.5" /></button>
                    <button
                      onClick={cancelMove}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500"
                      title="Cancel"
                      data-testid={`move-cancel-${b.id}`}
                    ><X weight="bold" className="w-3.5 h-3.5" /></button>
                  </div>
                ) : editingBranchId === b.id ? (
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Input className="h-8 text-xs" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name" />
                    <Input className="h-8 text-xs" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} placeholder="Address (optional)" />
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{b.name}</p>
                    {b.address && <p className="text-[11px] text-slate-500 truncate">{b.address}</p>}
                    <p className="text-[10px] text-slate-400 mt-0.5">{b.member_count} member{b.member_count === 1 ? "" : "s"}</p>
                  </div>
                )}
                <div className="flex items-center gap-1 shrink-0">
                  {deletingBranchId === b.id || movingBranchId === b.id ? null : editingBranchId === b.id ? (
                    <>
                      <button onClick={commitEdit} className="w-7 h-7 flex items-center justify-center rounded hover:bg-emerald-100 text-emerald-600" data-testid={`branch-save-${b.id}`}><Check weight="bold" className="w-3.5 h-3.5" /></button>
                      <button onClick={cancelEdit} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500" data-testid={`branch-cancel-${b.id}`}><X weight="bold" className="w-3.5 h-3.5" /></button>
                    </>
                  ) : (
                    <>
                      {b.member_count > 0 && branches.length > 1 && (
                        <button
                          onClick={() => beginMove(b)}
                          className="w-7 h-7 flex items-center justify-center rounded hover:bg-indigo-100 text-slate-500 hover:text-indigo-600"
                          title={`Move ${b.member_count} member${b.member_count === 1 ? "" : "s"} to another branch`}
                          data-testid={`branch-move-${b.id}`}
                        ><ArrowsLeftRight weight="bold" className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => beginEdit(b)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500" data-testid={`branch-edit-${b.id}`}><PencilSimple weight="bold" className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDeleteBranch(b)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-100 text-slate-400 hover:text-red-600" data-testid={`branch-delete-${b.id}`}><Trash weight="bold" className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex gap-2 items-end">
          <div className="flex-1">
            <Input className="h-8 text-xs" placeholder="New branch name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-branch-name" />
          </div>
          <div className="flex-1">
            <Input className="h-8 text-xs" placeholder="Address (optional)" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} data-testid="new-branch-address" />
          </div>
          <Button onClick={handleCreate} disabled={!newName.trim() || creating} className="h-8 px-3 text-xs gap-1" data-testid="create-branch-btn" style={{ background: '#100f5d', color: 'white' }}>
            <Plus weight="bold" className="w-3 h-3" /> {creating ? "Adding…" : "Add"}
          </Button>
        </div>
      </div>

      {/* Per-referrer ACL */}
      <div className="border-t border-slate-200 pt-4">
        <Label className="text-sm font-semibold mb-2 block">Access for {referrer.name}</Label>
        <p className="text-[11px] text-slate-500 mb-3">
          Home branch is always visible. Tick extra branches the referrer can see on their portal's "All" view. "Branch admin" (Phase 2) lets the referrer self-manage members + branch overrides.
        </p>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Home branch</Label>
          <select
            value={homeBranch}
            onChange={(e) => setHomeBranch(e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-slate-200 text-sm bg-white"
            data-testid="home-branch-select"
          >
            <option value="">— None (no branch) —</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        {branches.length > 0 && (
          <div className="mt-3">
            <Label className="text-xs text-slate-600">Additional branches this referrer can see</Label>
            <ul className="mt-1.5 space-y-1.5">
              {branches.map((b) => {
                const isHome = b.id === homeBranch;
                const isAllowed = isHome || allowed.has(b.id);
                const isAdmin = adminOf.has(b.id);
                return (
                  <li key={b.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-slate-50" data-testid={`acl-row-${b.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={isAllowed}
                        onChange={() => !isHome && toggleAllowed(b.id)}
                        disabled={isHome}
                        className="w-4 h-4 rounded border-slate-300"
                        data-testid={`acl-allow-${b.id}`}
                      />
                      <span className={`text-xs truncate ${isHome ? "font-semibold text-indigo-700" : "text-slate-700"}`}>
                        {b.name} {isHome && <span className="text-[10px] font-normal">· home</span>}
                      </span>
                    </div>
                    <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={isAdmin}
                        onChange={() => toggleAdmin(b.id)}
                        className="w-3.5 h-3.5 rounded border-slate-300"
                        data-testid={`acl-admin-${b.id}`}
                      />
                      Branch admin
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <Button
          onClick={saveAcl}
          disabled={savingAcl}
          className="mt-4 h-9 px-4 text-white text-xs hover:opacity-90"
          style={{ background: '#100f5d' }}
          data-testid="save-branch-acl-btn"
        >
          {savingAcl ? "Saving…" : "Save Branch Access"}
        </Button>
      </div>
    </div>
  );
}
